import { formatDate } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";
import {
  Badge,
  Button,
  Checkbox,
  confirm,
  createSortableColumn,
  DataTable,
  Label,
  type TanstackReactTable,
} from "@devhop/ui";
import {
  CheckIcon,
  EditIcon,
  EyeIcon,
  PowerIcon,
  TrashIcon,
  XIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import type { ClassTemplateDTO } from "../../domain/contracts/templates";

type ClassTemplateRow = ClassTemplateDTO;

type ClassTemplateTableProps = {
  data: ClassTemplateRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onView: (template: ClassTemplateRow) => void;
  onEdit: (template: ClassTemplateRow) => void;
  onToggleStatus: (id: string, status: "active" | "inactive") => void;
  onDelete: (id: string) => void;
  onBulkDelete?: (ids: string[]) => void;
  onBulkToggleStatus?: (
    ids: string[],
    status: "active" | "inactive",
  ) => void;
};

export function ClassTemplateTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onView,
  onEdit,
  onToggleStatus,
  onDelete,
  onBulkDelete,
  onBulkToggleStatus,
}: ClassTemplateTableProps) {
  const [columnSelection, setColumnSelection] = useState<string[]>([]);

  // Clear selection when data changes (pagination, filter, etc.)
  useEffect(() => {
    setColumnSelection([]);
  }, [data, offset]);

  const selectedCount = columnSelection.length;
  const allSelected = data.length > 0 && selectedCount === data.length;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setColumnSelection(data.map((row) => row.id));
    } else {
      setColumnSelection([]);
    }
  };

  const handleBulkDelete = async () => {
    if (!onBulkDelete || selectedCount === 0) return;

    const ok = await confirm({
      title: "ລຶບ Template ຄລາດສ໌ຫຼາຍລາຍການ",
      description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບ template ${selectedCount} ລາຍການ?`,
      actionText: "ລຶບ",
      ActionProps: {
        variant: "destructive",
      },
    });

    if (ok) {
      onBulkDelete(columnSelection);
      setColumnSelection([]);
    }
  };

  const handleBulkToggleStatus = async (status: "active" | "inactive") => {
    if (!onBulkToggleStatus || selectedCount === 0) return;

    const statusLabel = status === "active" ? "ເປີດການໃຊ້ງານ" : "ປິດການໃຊ້ງານ";
    const ok = await confirm({
      title: `ປ່ຽນສະຖານະ Template ${selectedCount} ລາຍການ`,
      description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະ${statusLabel} template ${selectedCount} ລາຍການ?`,
      actionText: statusLabel,
    });

    if (ok) {
      onBulkToggleStatus(columnSelection, status);
      setColumnSelection([]);
    }
  };

  const columns: TanstackReactTable.ColumnDef<ClassTemplateRow>[] = [
    {
      enableSorting: false,
      accessorKey: "id",
      header: () => (
        <Checkbox
          checked={allSelected}
          onCheckedChange={handleSelectAll}
          aria-label="Select all"
          className="hidden md:block"
        />
      ),
      size: 50,
      cell: ({ row }) => (
        <Label className="flex items-center gap-2">
          <Checkbox
            checked={columnSelection.includes(row.original.id)}
            onCheckedChange={(checked) => {
              if (checked) {
                setColumnSelection([...columnSelection, row.original.id]);
              } else {
                setColumnSelection(
                  columnSelection.filter((id) => id !== row.original.id),
                );
              }
            }}
            aria-label={`Select ${row.original.classTypeName}`}
          />
          <span className="hidden font-medium text-sm max-md:block">
            ເລືອກ
          </span>
        </Label>
      ),
    },
    createSortableColumn("classTypeName", "ປະເພດຄລາດສ໌", {
      cell: ({ row }) => row.original.classTypeName,
    }),
    createSortableColumn("levelName", "ລະດັບ", {
      cell: ({ row }) => (
        <Badge variant="outline">{row.original.levelName}</Badge>
      ),
    }),
    createSortableColumn("duration", "ໄລຍະເວລາ", {
      cell: ({ row }) => `${row.original.duration} ນາທີ`,
    }),
    {
      header: "ຄຳອະທິບາຍ",
      accessorKey: "description",
      cell: ({ row }) => (
        <div className="max-w-md truncate">
          {row.original.description || "-"}
        </div>
      ),
    },
    {
      header: "ລົງທະບຽນອອນລາຍ",
      accessorKey: "onlineRegistration",
      cell: ({ row }) => (
        <Badge variant={row.original.onlineRegistration ? "default" : "outline"}>
          {row.original.onlineRegistration ? "ເປີດຮັບ" : "ປິດຮັບ"}
        </Badge>
      ),
    },
    {
      header: "ສະຖານະ",
      accessorKey: "status",
      cell: ({ row }) => {
        const status = row.original.status;
        return (
          <Badge variant={status === "active" ? "default" : "secondary"}>
            {status === "active" ? "ໃຊ້ງານ" : "ບໍ່ໃຊ້ງານ"}
          </Badge>
        );
      },
    },
    createSortableColumn("createdAt", "ວັນທີສ້າງ", {
      cell: ({ row }) => formatDate(row.original.createdAt),
    }),
    {
      id: "actions",
      header: "",
      cell: ({ row }: { row: { original: ClassTemplateRow } }) => {
        const template = row.original;
        const actions = [];

        actions.push({
          label: "ເບິ່ງລາຍລະອຽດ",
          icon: <EyeIcon className="h-4 w-4" />,
          onClick: () => onView(template),
        });

        actions.push({
          label: "ແກ້ໄຂ",
          icon: <EditIcon className="h-4 w-4" />,
          onClick: () => onEdit(template),
        });

        actions.push({
          label: template.status === "active" ? "ປິດການໃຊ້ງານ" : "ເປີດການໃຊ້ງານ",
          icon: <TrashIcon className="h-4 w-4" />,
          onClick: async () => {
            const ok = await confirm({
              title:
                template.status === "active"
                  ? "ປິດການໃຊ້ງານ Template"
                  : "ເປີດການໃຊ້ງານ Template",
              description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະ${
                template.status === "active" ? "ປິດ" : "ເປີດ"
              }ການໃຊ້ງານ template ນີ້?`,
              actionText: template.status === "active" ? "ປິດ" : "ເປີດ",
            });
            if (ok) {
              onToggleStatus(
                template.id,
                template.status === "active" ? "inactive" : "active",
              );
            }
          },
        });

        actions.push({
          label: "ລຶບ",
          variant: "destructive" as const,
          icon: <TrashIcon className="h-4 w-4" />,
          onClick: async () => {
            const ok = await confirm({
              title: "ລຶບ Template ຄລາດສ໌",
              description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບ template ນີ້?",
              actionText: "ລຶບ",
              ActionProps: {
                variant: "destructive",
              },
            });
            if (ok) onDelete(template.id);
          },
        });

        return <RowActions actions={actions} maxInline={2} />;
      },
    },
  ];

  return (
    <>
      {selectedCount > 0 && (
        <div className="flex flex-col gap-2 border-b bg-muted/50 px-4 py-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">
              ເລືອກ {selectedCount} ລາຍການ
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setColumnSelection([])}
            >
              <XIcon className="size-4" />
              <span className="hidden sm:inline">ຍົກເລີກ</span>
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {onBulkToggleStatus && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkToggleStatus("active")}
                  className="flex-1 sm:flex-initial"
                >
                  <CheckIcon className="size-4" />
                  <span className="hidden sm:inline">ເປີດການໃຊ້ງານ</span>
                  <span className="sm:hidden">ເປີດ</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkToggleStatus("inactive")}
                  className="flex-1 sm:flex-initial"
                >
                  <PowerIcon className="size-4" />
                  <span className="hidden sm:inline">ປິດການໃຊ້ງານ</span>
                  <span className="sm:hidden">ປິດ</span>
                </Button>
              </>
            )}
            {onBulkDelete && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleBulkDelete}
                className="flex-1 sm:flex-initial"
              >
                <TrashIcon className="size-4" />
                <span className="hidden sm:inline">ລຶບ ({selectedCount})</span>
                <span className="sm:hidden">ລຶບ</span>
              </Button>
            )}
          </div>
        </div>
      )}
      <DataTable<ClassTemplateRow, unknown>
        noDataMessage="ບໍ່ພົບ template ຄລາດສ໌"
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={(sorting) => {
          if (sorting[0]?.id === "") return;
          onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
        }}
      />
    </>
  );
}
